import { useEffect, useState } from 'react';
import type { EventSettings } from '../services/events';
import { CLOSE_COUNTDOWN_WINDOW_MS, requestsCloseAtMsOf } from '../utils/requestWindow';

const MAX_TIMEOUT_MS = 2 ** 31 - 1;
const TICK_MS = 1000;

function delayUntilNextRender(nowMs: number, closeAtMs: number): number {
  const remainingMs = closeAtMs - nowMs;
  if (remainingMs > CLOSE_COUNTDOWN_WINDOW_MS) return remainingMs - CLOSE_COUNTDOWN_WINDOW_MS;
  return remainingMs % TICK_MS || TICK_MS;
}

export function useRequestWindow(settings: Partial<EventSettings> | undefined): { nowMs: number; closeAtMs: number | null } {
  const closeAtMs = requestsCloseAtMsOf(settings);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (closeAtMs === null) return;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const renderAndReschedule = () => {
      const currentMs = Date.now();
      setNowMs(currentMs);
      if (currentMs >= closeAtMs) return;
      timer = setTimeout(renderAndReschedule, Math.min(delayUntilNextRender(currentMs, closeAtMs), MAX_TIMEOUT_MS));
    };

    renderAndReschedule();
    return () => clearTimeout(timer);
  }, [closeAtMs]);

  return { nowMs, closeAtMs };
}
